import { Smartphone } from 'lucide-react';
import { QRCode } from '../../../ui';
import { formatINR } from '../../../utils/format';
import CopyButton from './CopyButton';

/**
 * How to pay a manual UPI order: the QR (encodes payee, exact amount and order number),
 * a "Pay with UPI app" link for phones (buyers can't scan their own screen), and the details to pay by hand.
 *
 * instructions come from the server: { upiUri, upiId, payeeName, amount, currency, reference }.
 */
const UpiPaymentPanel = ({ instructions, amount, orderId }) => {
  const { upiUri, upiId } = instructions;
  const payeeName = instructions.payeeName || 'Projects Kart';
  const amountText = formatINR(amount);
  // Plain number for pasting into a UPI app ("1199.00").
  const amountPlain = instructions.amount || String(amount);

  return (
    <>
      <section className="pk-card co-pay" aria-labelledby="co-pay-title">
        <div className="co-pay__amount">
          <h2 className="co-pay__label" id="co-pay-title">
            Amount to pay
          </h2>
          <p className="co-pay__value">{amountText}</p>
        </div>

        <div className="co-pay__body">
          <div className="co-pay__qr">
            <QRCode
              value={upiUri}
              size={208}
              title={`UPI QR code: pay ${amountText} to ${payeeName} for order ${orderId}`}
            />
            <p className="pk-hint">Scan with any UPI app</p>
          </div>

          <a className="pk-btn pk-btn--block co-upi-btn" href={upiUri}>
            <Smartphone aria-hidden="true" />
            Pay with UPI app
          </a>

          <dl className="pk-rows co-pay__details">
            <div>
              <dt>Pay to</dt>
              <dd>{payeeName}</dd>
            </div>
            <div>
              <dt>UPI ID</dt>
              <dd className="co-copyable">
                <span className="co-mono">{upiId}</span>
                <CopyButton value={upiId} label="Copy UPI ID" />
              </dd>
            </div>
            <div>
              <dt>Amount</dt>
              <dd className="co-copyable">
                <span>{amountText}</span>
                <CopyButton value={amountPlain} label="Copy amount" />
              </dd>
            </div>
            <div>
              <dt>Order number</dt>
              <dd className="co-copyable">
                <span className="co-mono">{orderId}</span>
                <CopyButton value={orderId} label="Copy order number" />
              </dd>
            </div>
          </dl>
        </div>
      </section>

      <section className="pk-card" aria-labelledby="co-steps-title">
        <h2 className="pk-card__title co-card-title" id="co-steps-title">
          How to pay
        </h2>
        <ol className="co-steps">
          <li>
            Scan the QR code with any UPI app, or tap <strong>Pay with UPI app</strong> on your
            phone.
          </li>
          <li>
            Check the payee is {payeeName} and pay exactly {amountText}. Paying to the UPI ID by
            hand? Add <span className="co-mono">{orderId}</span> as the note.
          </li>
          <li>
            Open the payment in your UPI app and copy the UPI transaction ID (UTR), usually 12
            digits.
          </li>
          <li>
            Enter it below. We verify each payment by hand, typically within 12 hours. You then
            get the project's deliverables from your order page.
          </li>
        </ol>
      </section>
    </>
  );
};

export default UpiPaymentPanel;
